import { SharedRef, type SharedRefType } from "expo";
import { Platform } from "react-native";
import {
  buildResizedImageUri,
  isResizableImageUri,
  stableImageCacheKey,
} from "./responsiveSource";
import type { ImageProps } from "./index";

type ImageSource = ImageProps["source"] | string | undefined;
type NormalizedSource = ReturnType<typeof normalizeSource>;

export type LoadStatus = "loading" | "loaded" | "failed";
export type SettledLoad = {
  sourceKey: string;
  status: Exclude<LoadStatus, "loading">;
};
export type BoxSize = { width: number; height: number };

const IMAGE_FORMAT = Platform.OS === "web" ? "auto" : "webp";

// A reference to an already-decoded native image (`expo-video`'s `VideoThumbnail`,
// `expo-image`'s `useImage`/`Image.loadAsync`). Same check `expo-image` itself uses
// to tell a ref apart from an `{ uri }` source, typed as its `source` prop
// declares a ref (`SharedRefType`, the instance type).
const isImageRef = (value: unknown): value is SharedRefType<"image"> =>
  value instanceof SharedRef && value.nativeRefType === "image";

export const asImageRef = (source: unknown): SharedRefType<"image"> | null =>
  isImageRef(source) ? source : null;

const hasUri = <S>(source: S): source is Extract<S, object> & { uri: string } =>
  typeof source === "object" &&
  source !== null &&
  "uri" in source &&
  typeof source.uri === "string" &&
  source.uri !== "";

export const normalizeSource = (source: ImageSource) => {
  if (typeof source === "number") return source;
  if (!source) return undefined;
  const newSource = typeof source === "string" ? { uri: source } : source;
  // A readonly array of sources has no `uri`; `Array.isArray` doesn't narrow it.
  if (!("uri" in newSource)) return newSource;
  if (!newSource.uri) return undefined;
  return newSource;
};

// Metro bundles a `require()`d image as a module id on native, and on web as
// `{ uri: "/assets/…", width, height }` or a bare path: a uri with no scheme.
export const isBundledAsset = (source: NormalizedSource) => {
  if (typeof source === "number") return true;
  if (!hasUri(source)) return false;
  return (
    !source.uri.startsWith("//") && !/^[a-z][a-z\d+.-]*:/i.test(source.uri)
  );
};

// Only the media CDN gets a width; every other uri (presigned, local, external)
// is passed through untouched. A resizable image whose width isn't known yet gets
// no source.
export const resizeSource = (
  source: NormalizedSource,
  width: number | null,
) => {
  if (!hasUri(source) || !isResizableImageUri(source.uri)) return source;
  if (width === null) return undefined;
  return {
    ...source,
    uri: buildResizedImageUri(source.uri, width, IMAGE_FORMAT),
  };
};

// Signed urls get a new signature every day; keying the image cache without the
// signing params lets the re-signed url hit the memory/disk cache instead of
// downloading the same image again. An explicit `cacheKey` wins.
export const withStableCacheKey = (source: ReturnType<typeof resizeSource>) => {
  if (!hasUri(source)) return source;
  if ("cacheKey" in source && source.cacheKey) return source;
  const cacheKey = stableImageCacheKey(source.uri);
  return cacheKey ? { ...source, cacheKey } : source;
};

// Keyed by the source uri, not the resized one, so stepping up to a wider
// image doesn't bring the placeholder back.
export const sourceKeyOf = (source: NormalizedSource) => {
  if (source === undefined) return null;
  if (hasUri(source)) return source.uri;
  return "source";
};

// No source is final ("failed"); a source is "loading" until expo-image
// reports on that same source. Derived rather than reset in an effect, so a
// new uri never renders a frame with the previous uri's status.
export const resolveLoadStatus = (
  sourceKey: string | null,
  settled: SettledLoad | null,
): LoadStatus => {
  if (sourceKey === null) return "failed";
  if (settled && settled.sourceKey === sourceKey) return settled.status;
  return "loading";
};
